// Ad export (M14, DRAW_PLAN D10 reduced): PNG and PDF of the first artboard.
//   PNG: the same display list and the same paintItem() as the on-screen tiles, on an OffscreenCanvas at
//        the export scale (screen formats 1× / 2×, print formats 150 / 300 dpi); 8-bit sRGB with an sRGB ICC.
//   PDF: vector. Shapes and text are paths (text = glyph outlines, so no font is embedded), photos are
//        embedded JPEG/PNG clipped to their frame. Print formats get 9 pt bleed, crop marks and
//        TrimBox/BleedBox. Solid paints are vector; an object with a gradient or an effect (M20: soft bands,
//        cut-out shadows) is placed as an image of the canvas rendering at the largest export size.
import {
  appendBezierCurve,
  clip,
  closePath,
  endPath,
  lineTo,
  moveTo,
  PDFDocument,
  popGraphicsState,
  pushGraphicsState,
  rgb as pdfRgb,
  type PDFOperator,
  type PDFPage,
} from 'pdf-lib';
import type { Doc, Mat2D, Subpath } from '../model/types';
import { DisplayList, geometry, type Item } from '../render/displayList';
import { cssColor, paintItem, resolveColor } from '../render/paint';
import { ensureBitmaps, getAsset } from '../assets';
import { loadFonts } from '../text/fonts';
import { layoutText, type Cmd } from '../text/layout';
import { imageDrawRect, framePath } from '../render/adPaint';
import { encodePng } from '../../io/encode/png';
import { makeIccProfile, PROFILE_NAMES } from '../../color/icc';
import { shapeToSubpaths } from '../geometry/bezier';
import { isWebp } from '../../io/decode/webp';
import { readExif } from '../../io/exif';

export type ExportScale = { id: string; scale: number; dpi: number; label: string };

/** Export sizes offered for a document: screen formats 1× / 2×, print formats 150 / 300 dpi. */
export function exportScales(doc: Doc): ExportScale[] {
  const ab = doc.artboards[0]!;
  const print = doc.rasterDpi >= 150;
  if (print)
    return [150, 300].map((dpi) => ({
      id: `${dpi}dpi`,
      scale: dpi / 72,
      dpi,
      label: `${Math.round((ab.w * dpi) / 72)} × ${Math.round((ab.h * dpi) / 72)} px (${dpi} dpi)`,
    }));
  return [1, 2].map((k) => ({
    id: `${k}x`,
    scale: k,
    dpi: 72 * k,
    label: `${Math.round(ab.w * k)} × ${Math.round(ab.h * k)} px`,
  }));
}

/** Draws the first artboard of `doc` at `scale` (px per pt). Fonts and images must be ready. */
export function drawArtboard(g: OffscreenCanvasRenderingContext2D, doc: Doc, scale: number) {
  const ab = doc.artboards[0]!;
  const list = new DisplayList();
  list.update(doc);
  g.save();
  g.setTransform(scale, 0, 0, scale, -ab.x * scale, -ab.y * scale);
  g.fillStyle = ab.background?.kind === 'solid' ? cssColor(ab.background.color, doc) : '#fff';
  g.fillRect(ab.x, ab.y, ab.w, ab.h);
  for (const it of list.query({ x: ab.x, y: ab.y, w: ab.w, h: ab.h }))
    if (!it.hidden) paintItem(g as unknown as CanvasRenderingContext2D, it, doc);
  g.restore();
}

export async function renderArtboard(doc: Doc, scale: number): Promise<OffscreenCanvas> {
  await loadFonts();
  await ensureBitmaps(doc);
  const ab = doc.artboards[0]!;
  const c = new OffscreenCanvas(Math.max(1, Math.round(ab.w * scale)), Math.max(1, Math.round(ab.h * scale)));
  drawArtboard(c.getContext('2d')!, doc, scale);
  return c;
}

export async function exportPng(doc: Doc, s: ExportScale): Promise<Uint8Array> {
  const c = await renderArtboard(doc, s.scale);
  const data = c.getContext('2d')!.getImageData(0, 0, c.width, c.height);
  return encodePng({
    width: c.width,
    height: c.height,
    data: new Uint8Array(data.data.buffer),
    bitDepth: 8,
    channels: 'auto',
    icc: { name: PROFILE_NAMES.srgb, profile: makeIccProfile('srgb') },
    dpi: s.dpi,
  });
}

// ───────────────────────────── PDF ─────────────────────────────

type P = [number, number];
const apply = (m: Mat2D, x: number, y: number): P => [m[0] * x + m[2] * y + m[4], m[1] * x + m[3] * y + m[5]];

/** A path in page space (y up): the PDF clip operators and the SVG data for drawSvgPath come from it. */
type Seg = { t: 'M' | 'L'; p: P } | { t: 'C'; a: P; b: P; p: P } | { t: 'Z' };

function subpathSegs(sps: Subpath[], toPage: (x: number, y: number) => P): Seg[] {
  const out: Seg[] = [];
  for (const sp of sps) {
    const pts = sp.points;
    if (!pts.length) continue;
    out.push({ t: 'M', p: toPage(pts[0]!.x, pts[0]!.y) });
    const n = sp.closed ? pts.length : pts.length - 1;
    for (let i = 0; i < n; i++) {
      const p = pts[i]!,
        q = pts[(i + 1) % pts.length]!;
      out.push({ t: 'C', a: toPage(p.outX, p.outY), b: toPage(q.inX, q.inY), p: toPage(q.x, q.y) });
    }
    if (sp.closed) out.push({ t: 'Z' });
  }
  return out;
}

function cmdSegs(cmds: Cmd[], toPage: (x: number, y: number) => P): Seg[] {
  const out: Seg[] = [];
  let cx = 0,
    cy = 0;
  for (const c of cmds) {
    if (c.type === 'M' || c.type === 'L') {
      out.push({ t: c.type, p: toPage(c.x, c.y) });
      cx = c.x;
      cy = c.y;
    } else if (c.type === 'Q') {
      // Quadratic → cubic (exact).
      out.push({
        t: 'C',
        a: toPage(cx + (2 / 3) * (c.x1 - cx), cy + (2 / 3) * (c.y1 - cy)),
        b: toPage(c.x + (2 / 3) * (c.x1 - c.x), c.y + (2 / 3) * (c.y1 - c.y)),
        p: toPage(c.x, c.y),
      });
      cx = c.x;
      cy = c.y;
    } else if (c.type === 'C') {
      out.push({ t: 'C', a: toPage(c.x1, c.y1), b: toPage(c.x2, c.y2), p: toPage(c.x, c.y) });
      cx = c.x;
      cy = c.y;
    } else out.push({ t: 'Z' });
  }
  return out;
}

const segOps = (segs: Seg[]): PDFOperator[] =>
  segs.map((s) =>
    s.t === 'M'
      ? moveTo(s.p[0], s.p[1])
      : s.t === 'L'
        ? lineTo(s.p[0], s.p[1])
        : s.t === 'C'
          ? appendBezierCurve(s.a[0], s.a[1], s.b[0], s.b[1], s.p[0], s.p[1])
          : closePath(),
  );

/** SVG path data (y down from the page top) for drawSvgPath at origin (0, H). */
function segSvg(segs: Seg[], H: number): string {
  const f = (p: P) => `${+p[0].toFixed(3)} ${+(H - p[1]).toFixed(3)}`;
  return segs
    .map((s) => (s.t === 'Z' ? 'Z' : s.t === 'C' ? `C${f(s.a)} ${f(s.b)} ${f(s.p)}` : `${s.t}${f(s.p)}`))
    .join('');
}

type Rgba = { rgb: [number, number, number]; alpha: number };
function paintSegs(
  page: PDFPage,
  segs: Seg[],
  o: { fill?: Rgba; stroke?: Rgba & { width: number; dash: number[]; cap: 'butt' | 'round' | 'square' } },
) {
  const H = page.getHeight();
  const d = segSvg(segs, H);
  if (o.fill) page.drawSvgPath(d, { x: 0, y: H, color: pdfRgb(...o.fill.rgb), opacity: o.fill.alpha });
  if (o.stroke && o.stroke.width > 0)
    page.drawSvgPath(d, {
      x: 0,
      y: H,
      borderColor: pdfRgb(...o.stroke.rgb),
      borderOpacity: o.stroke.alpha,
      borderWidth: o.stroke.width,
      borderDashArray: o.stroke.dash.length ? o.stroke.dash : undefined,
      borderLineCap: o.stroke.cap === 'round' ? 1 : o.stroke.cap === 'square' ? 2 : 0,
    });
}

export async function exportPdf(doc: Doc): Promise<Uint8Array> {
  await loadFonts();
  await ensureBitmaps(doc);
  const ab = doc.artboards[0]!;
  const print = doc.rasterDpi >= 150;
  const bleed = print ? ab.bleed.left || 9 : 0;
  const slug = print ? 18 : 0; // room for crop marks
  const m = bleed + slug;
  const pdf = await PDFDocument.create();
  pdf.setTitle(doc.name);
  pdf.setCreator('EAT Photo Booth');
  pdf.setProducer('EAT Photo Booth (pdf-lib)');
  const page = pdf.addPage([ab.w + 2 * m, ab.h + 2 * m]);
  const H = page.getHeight();
  const toPage = (x: number, y: number): P => [x - ab.x + m, H - (y - ab.y + m)];
  if (print) {
    page.setTrimBox(m, m, ab.w, ab.h);
    page.setBleedBox(slug, slug, ab.w + 2 * bleed, ab.h + 2 * bleed);
  }
  // Background over the trim + bleed.
  if (ab.background?.kind === 'solid') {
    const c = resolveColor(ab.background.color, doc);
    page.drawRectangle({
      x: slug,
      y: slug,
      width: ab.w + 2 * bleed,
      height: ab.h + 2 * bleed,
      color: pdfRgb(...c.rgb),
      opacity: c.alpha,
    });
  }
  const list = new DisplayList();
  list.update(doc);
  const embedded = new Map<string, Awaited<ReturnType<typeof pdf.embedPng>>>();
  for (const it of list.query({
    x: ab.x - bleed,
    y: ab.y - bleed,
    w: ab.w + 2 * bleed,
    h: ab.h + 2 * bleed,
  })) {
    if (it.hidden) continue;
    const k = print ? 300 / 72 : 2;
    if (needsRaster(it, k)) await pdfRaster(pdf, page, it, doc, toPage, k, bleed);
    else await pdfItem(pdf, page, it, doc, toPage, embedded);
  }
  if (print) cropMarks(page, m, ab.w, ab.h, bleed);
  return pdf.save();
}

/**
 * M20: an object with a gradient fill or an enabled effect (soft gradient band, cut-out shadow); M21: a bite,
 * or a photo in a template crop (Ideas close-ups) enlarged past its own pixels at the export size: PDF viewers
 * draw an enlarged image unsmoothed (blocky), the canvas draws it smooth, so it goes in as the canvas rendering.
 */
function needsRaster(it: Item, k: number): boolean {
  const a = it.node.appearance;
  const n = it.node;
  if (n.kind === 'image' && n.bite) return true;
  // M21b: a mirrored image, or one turned on the page (a tilted print), goes in as the canvas rendering.
  if (n.kind === 'image' && (n.flipX || n.transform[1] || n.transform[2])) return true;
  if (n.kind === 'image' && n.crop && n.iw && imageDrawRect(n).w * k > n.iw * 1.05) return true;
  return a.effects.some((e) => e.enabled) || a.fills.some((f) => f.paint.kind !== 'solid');
}

/**
 * M20: such an object goes into the PDF as an image of exactly what the canvas draws (same paintItem), at the
 * largest export size (300 dpi print, 2× screen), over the object's bounds clipped to the page. Everything
 * else stays vector.
 */
async function pdfRaster(
  pdf: PDFDocument,
  page: PDFPage,
  it: Item,
  doc: Doc,
  toPage: (x: number, y: number) => P,
  scale: number,
  bleed: number,
) {
  const ab = doc.artboards[0]!;
  // Clipped to the bleed, not the trim: an object the template runs off the edge must print into the bleed.
  const x0 = Math.max(ab.x - bleed, it.bounds.x),
    y0 = Math.max(ab.y - bleed, it.bounds.y),
    x1 = Math.min(ab.x + ab.w + bleed, it.bounds.x + it.bounds.w),
    y1 = Math.min(ab.y + ab.h + bleed, it.bounds.y + it.bounds.h);
  if (x1 <= x0 || y1 <= y0) return;
  const c = new OffscreenCanvas(Math.ceil((x1 - x0) * scale), Math.ceil((y1 - y0) * scale));
  const g = c.getContext('2d')!;
  g.setTransform(scale, 0, 0, scale, -x0 * scale, -y0 * scale);
  paintItem(g as unknown as CanvasRenderingContext2D, it, doc);
  const img = await pdf.embedPng(
    new Uint8Array(await (await c.convertToBlob({ type: 'image/png' })).arrayBuffer()),
  );
  const p0 = toPage(x0, y1); // bottom-left in page space (y up)
  page.drawImage(img, { x: p0[0], y: p0[1], width: x1 - x0, height: y1 - y0 });
}

/**
 * pdf-lib embeds only PNG and JPEG. A WebP photo or logo (Design slot, My brand) used to go to embedJpg
 * and Save PDF failed; it is now converted to PNG first (the browser decodes it; alpha is kept).
 * Checked by the bytes, not the stored type: the type comes from the file picker and can be empty.
 */
async function embedAsset(pdf: PDFDocument, asset: { bytes: Uint8Array; type: string }) {
  const kind = imageKind(asset.bytes);
  if (kind === 'png') return pdf.embedPng(asset.bytes);
  // A phone JPEG dropped into a slot keeps its EXIF orientation; PDF viewers ignore it, while the slot was sized
  // from the browser's (rotated) decode. Such a JPEG goes in through the rotated bitmap, like a WebP.
  if (kind === 'jpeg' && (await readExif(asset.bytes)).orientation === 1) return pdf.embedJpg(asset.bytes);
  const bmp = await createImageBitmap(
    new Blob([asset.bytes as Uint8Array<ArrayBuffer>], { type: asset.type || undefined }),
    { premultiplyAlpha: 'none' },
  );
  const c = new OffscreenCanvas(bmp.width, bmp.height);
  c.getContext('2d')!.drawImage(bmp, 0, 0);
  bmp.close();
  if (kind === 'jpeg')
    return pdf.embedJpg(
      new Uint8Array(await (await c.convertToBlob({ type: 'image/jpeg', quality: 0.95 })).arrayBuffer()),
    );
  return pdf.embedPng(new Uint8Array(await (await c.convertToBlob({ type: 'image/png' })).arrayBuffer()));
}

/** What the bytes are (the stored MIME type comes from the file picker and can be empty or wrong). */
export function imageKind(b: Uint8Array): 'png' | 'jpeg' | 'webp' | 'other' {
  if (b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47) return 'png';
  if (b[0] === 0xff && b[1] === 0xd8) return 'jpeg';
  return isWebp(b) ? 'webp' : 'other';
}

async function pdfItem(
  pdf: PDFDocument,
  page: PDFPage,
  it: Item,
  doc: Doc,
  toPage: (x: number, y: number) => P,
  embedded: Map<string, Awaited<ReturnType<PDFDocument['embedPng']>>>,
) {
  const n = it.node;
  const w = it.world;
  const tp = (x: number, y: number) => {
    const [a, b] = apply(w, x, y);
    return toPage(a, b);
  };
  const scaleW = Math.sqrt(Math.abs(w[0] * w[3] - w[1] * w[2])) || 1;
  const k = n.appearance.opacity;
  const solid = (p: { kind: string; color?: unknown }, alpha: number) => {
    if (p.kind !== 'solid') return undefined;
    const c = resolveColor((p as { color: Parameters<typeof resolveColor>[0] }).color, doc);
    return { rgb: c.rgb, alpha: c.alpha * alpha * k };
  };
  if (n.kind === 'text') {
    const lay = layoutText(n);
    if (!lay || !lay.commands.length) return;
    const c = resolveColor(n.style.color, doc);
    paintSegs(page, cmdSegs(lay.commands, tp), { fill: { rgb: c.rgb, alpha: c.alpha * k } });
    return;
  }
  if (n.kind === 'image') {
    if (n.role === 'logo' && !n.assetId) return; // empty logo slot: nothing (same as the canvas)
    const segs = subpathSegs(framePath(n), tp);
    for (const f of n.appearance.fills) {
      const s = solid(f.paint, f.opacity);
      if (s) paintSegs(page, segs, { fill: s });
    }
    const asset = n.assetId ? getAsset(n.assetId) : undefined;
    if (asset && n.iw && n.ih) {
      let img = embedded.get(n.assetId!);
      if (!img) {
        img = await embedAsset(pdf, asset);
        embedded.set(n.assetId!, img);
      }
      const r = imageDrawRect(n);
      const p0 = tp(r.x, r.y + r.h); // bottom-left in page space (y up)
      page.pushOperators(pushGraphicsState(), ...segOps(segs), clip(), endPath());
      page.drawImage(img, { x: p0[0], y: p0[1], width: r.w * scaleW, height: r.h * scaleW, opacity: k });
      page.pushOperators(popGraphicsState());
    } else if (n.role === 'photo') {
      paintSegs(page, segs, { fill: { rgb: [0.85, 0.85, 0.85], alpha: k } });
    }
    for (const s of n.appearance.strokes) {
      const c = solid(s.paint, s.opacity);
      if (c)
        paintSegs(page, segs, {
          stroke: { ...c, width: s.width * scaleW, dash: s.dash.map((d) => d * scaleW), cap: s.cap },
        });
    }
    return;
  }
  // Shapes and paths.
  const sps = n.kind === 'shape' ? shapeToSubpaths(n.shape) : geometry(n).subpaths;
  if (!sps.length) return;
  const segs = subpathSegs(sps, tp);
  for (const f of n.appearance.fills) {
    const s = solid(f.paint, f.opacity);
    if (s) paintSegs(page, segs, { fill: s });
  }
  for (const s of n.appearance.strokes) {
    const c = solid(s.paint, s.opacity);
    if (c && s.width > 0)
      paintSegs(page, segs, {
        stroke: { ...c, width: s.width * scaleW, dash: s.dash.map((d) => d * scaleW), cap: s.cap },
      });
  }
}

/** Crop marks at the trim corners, outside the bleed (0.25 pt, registration black). */
function cropMarks(page: PDFPage, m: number, w: number, h: number, bleed: number) {
  const len = 12,
    gap = bleed + 3;
  const t = 0.25,
    color = pdfRgb(0, 0, 0);
  const xs = [m, m + w],
    ys = [m, m + h];
  for (const x of xs)
    for (const y of ys) {
      const sx = x === m ? -1 : 1,
        sy = y === m ? -1 : 1;
      page.drawLine({
        start: { x: x + sx * gap, y },
        end: { x: x + sx * (gap + len), y },
        thickness: t,
        color,
      });
      page.drawLine({
        start: { x, y: y + sy * gap },
        end: { x, y: y + sy * (gap + len) },
        thickness: t,
        color,
      });
    }
}
